/**
 * Deterministic "Report a bug" form — the reliable backbone that does NOT depend
 * on the model deciding to call report_bug. Launched from the drawer header.
 * Submits through the same SupportClient.reportBug path (Crisp ticket + Sentry
 * feedback). Diagnostics are attached by the client; the reporter's IDENTITY is
 * stamped server-side from the session and is deliberately NOT sent to Sentry
 * (see SupportClient.reportBug, IT-29).
 */
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ModalWrapper } from "@/components/ModalWrapper/ModalWrapper";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { useToast } from "@/components/Toast/Toast";
import { useAiAssistant } from "@/providers/AiAssistantProvider";
import { useAiAvailabilityStore } from "@/lib/ai/ai-store";
import { reportBug } from "@/lib/api/clients/SupportClient";

type Severity = "low" | "normal" | "high";

export function BugReportModal({ onClose }: { onClose: () => void }) {
  const t = useTranslations("aiAssistant.support");
  const { showToast } = useToast();
  const { scopeEventId, getConversationId } = useAiAssistant();

  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<Severity>("normal");
  const [submitting, setSubmitting] = useState(false);

  // The drawer (which owns this modal) can close/unmount mid-request — guard
  // against a state update on an unmounted component.
  const mountedRef = useRef(true);
  useEffect(() => {
    // Re-assert on mount — StrictMode (dev) runs mount→unmount→mount on the same
    // instance, so a cleanup-only effect would leave this permanently false.
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const submit = async () => {
    const trimmed = message.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    const surface = useAiAvailabilityStore.getState().builderSurface;
    try {
      await reportBug({
        message: trimmed,
        severity,
        context: {
          eventId: scopeEventId,
          conversationId: getConversationId(),
          surface: surface ? `builder:${surface}` : null,
        },
      });
      showToast(t("success"), "success");
      if (mountedRef.current) onClose();
    } catch {
      showToast(t("error"), "error");
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const severities: Severity[] = ["low", "normal", "high"];

  return (
    <ModalWrapper
      onClose={submitting ? () => {} : onClose}
      backdropClosable={!submitting}
      zIndex="aiOverlay"
      size="max-w-110"
    >
      <div className="surface-primary rounded-2xl border outline-quaternary p-5 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-medium">{t("modalTitle")}</h2>
          <p className="text-sm font-tertiary">{t("modalDescription")}</p>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t("messageLabel")}</span>
          <textarea
            autoFocus
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("messagePlaceholder")}
            rows={5}
            maxLength={4000}
            className="w-full resize-none rounded-xl border outline-quaternary surface-secondary px-3 py-2 text-sm focus-ring"
          />
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t("severityLabel")}</span>
          <div className="flex gap-2">
            {severities.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSeverity(s)}
                className={`press flex-1 rounded-xl border px-3 py-2 text-sm transition-colors cursor-pointer ${
                  severity === s
                    ? "outline-brand surface-builder-accent-light font-primary"
                    : "outline-quaternary surface-secondary hover:surface-tertiary font-tertiary"
                }`}
              >
                {t(s === "low" ? "severityLow" : s === "high" ? "severityHigh" : "severityNormal")}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <SecondaryButton
            text={t("cancel")}
            onClick={onClose}
            disabled={submitting}
            width="w-max"
            size="small"
          />
          <PrimaryButton
            text={submitting ? t("submitting") : t("submit")}
            onClick={submit}
            disabled={!message.trim() || submitting}
            loading={submitting}
            width="w-max"
            size="small"
          />
        </div>
      </div>
    </ModalWrapper>
  );
}
